import { useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine } from '../../components/ErrorPanel';
import { saveBlob } from '../../components/ExportButton';
import { Button, Card, PageHeader, SelectField, TextField } from '../../components/ui';
import { BillingExportQuery, PLAT } from './api';
import { BillingTabs } from './parts';

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Platform: CSV of invoices or payments for a date range, for the accounts package. */
export function ExportPage() {
  const now = new Date();
  const [kind, setKind] = useState<'invoices' | 'payments'>('invoices');
  const [from, setFrom] = useState(iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))));
  const [to, setTo] = useState(iso(now));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const valid = BillingExportQuery.safeParse({ kind, from, to }).success && from <= to;
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await api.download(`${PLAT}/export`, { query: { kind, from, to } });
      saveBlob(blob, filename ?? `billing-${kind}-${from}-${to}.csv`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <PageHeader title={t('billingUi.export.title')} description={t('billingUi.export.description')} />
      <BillingTabs />
      <Card
        footer={
          <Button variant="primary" disabled={!valid || busy} onClick={() => void run()}>
            <Download aria-hidden="true" className="h-4 w-4" />
            {busy ? t('widget.export.working') : t('billingUi.export.download')}
          </Button>
        }
      >
        <ErrorLine error={error} />
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField label={t('billingUi.export.what')} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="invoices">{t('billingUi.export.invoices')}</option>
            <option value="payments">{t('billingUi.export.payments')}</option>
          </SelectField>
          <TextField label={t('billingUi.export.from')} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField
            label={t('billingUi.export.to')}
            type="date"
            value={to}
            error={from > to ? t('billingUi.export.rangeError') : undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </Card>
    </>
  );
}
